/* Analýza V2+ — SLOUPCE TABULKY ZDROJŮ + POLE DO BUBLINY.
 *
 * PROČ TO EXISTUJE (nález Owner UAT, 7/2026):
 * Zdroje byly kompaktní řádek + tlačítko „Detail". Poradce tedy musel na
 * schůzce u KAŽDÉ smlouvy nejdřív kliknout, teprve pak psát. Majitel to vrátil
 * doslova takto: „Já jsem chtěl, aby tam byla možnost rovnou napsat. Nemusí
 * poradce klikat na žádný detail." A dodal: „jak běžně, tak i v tom prezenčním".
 *
 * DRUHÉ KOLO UAT (8/2026) — řádek byl naopak PŘÍLIŠ ŠIROKÝ:
 * „Musí to být jednoduchý, nesmí tam být moc políček. Když je ten text krátký,
 * tak tam není potřeba políčko, co zabírá hodně místa. Jenom ty důležitý
 * parametry, co poradce vyplní." U stavebka vyjmenoval, co chce vidět:
 * označení, instituce, aktuální hodnota, kolik posílá pravidelně, datum
 * založení, datum ukončení. A zbytek: „Číslo smlouvy, poznámka, další
 * informace — když klikne, tak tam vyskočí bublina."
 *
 * Modul proto skládá DVĚ sady ze stejné sady polí typu produktu:
 *   • sloupceZdroje() → co je vždy vidět v řádku (V_RADKU),
 *   • poleBubliny()   → všechno ostatní, do panelu za tlačítkem ⋯.
 *
 * ⚠️ PŘESUN DO BUBLINY NENÍ SMAZÁNÍ. Klíč i způsob zápisu (setM) zůstávají —
 * pole se jen kreslí jinde. Sjednocení obou sad musí pokrýt celou sadu polí
 * typu produktu; hlídá to __tests__/zdrojeSloupce.test.js.
 *
 * Sada polí zůstává majetkem typu produktu (produktyPole.js) — proto se sem
 * klíče nikdy nepřepisují, jen se řadí a doplní se dvě pole, která v sadě
 * polí být nemůžou:
 *   • currency  — měna se ukládá do `currency`, ne do `mena` (to je jen popisek
 *                 sady polí; poleDoKarty ho odjakživa vyhazuje ven),
 *   • __base    — DOPOČET hodnoty v základní měně, tedy read-only sloupec.
 *
 * ⚠️ KLÍČE SE NEPŘEJMENOVÁVAJÍ. lib/bilance.js sčítá měsíční odkládání přes
 * S(m.penzijko,"zam") a S(m.*,"vklad"). Když se klíč přejmenuje, formulář se
 * tváří správně, uloží se, testy svítí — a poradci tiše zmizí tisíce korun
 * z bilance. Hlídá to __tests__/kliceProtiBilanci.test.js.
 *
 * Čisté funkce, žádný React.
 */

import { polePodleTypu } from "./produktyPole";
import { poleNefin, kliceNefin, nabidkaTypu, TYPY_NEFIN, NEMOVITOST_BYDLENI, maPrijem, jeNajemni, POPISEK_PRIJMU } from "./nefinancni";
import { MENY } from "./meny";

/** Klíč dopočteného sloupce (hodnota v základní měně). Není to datové pole. */
export const SLOUPEC_BASE = "__base";

/* ===================================================================
   ROZMĚRY TABULKY — JEDEN ZDROJ PRO ŠÍŘKY SLOUPCŮ I PRO KOMPONENTU
   (audit 3. 10. 2026, nálezy A3.4 a A3.5)

   Změřeno na snímcích: hlavičky měly 9,5 px, pole 12,5 px písma a 32 px
   výšky. V prezentaci se zůstatek hypotéky ukázal jako „3 200 00“ (klient
   četl 320 tisíc) — sloupec na 88 px se do pole s odsazením nevešel.
   Šířky sloupců a písmo proto žijí na JEDNOM místě: komponenta
   (components/analyza/ZdrojeTabulka.js) kreslí přesně to, s čím se tu počítá,
   a `sirkaNaCastku` z toho spočítá, kolik místa číslo opravdu potřebuje.
=================================================================== */

/** Písmo v poli — běžný řádek / prezentace a úzké okno (px). */
export const PISMO_POLE = 14;
export const PISMO_POLE_KOMPAKT = 13;
/** Písmo hlavičky sloupce a popisků v bublině (px). 12 = popisek pole VELKÝMI
 *  (čitelnost 5. 10. 2026: 11 px bylo u stolu malé — 94 hlaviček ve Zdrojích
 *  a Závazcích; dekorativní nadpis smí mít 11, popisek pole ne). */
export const PISMO_HLAVICKY = 12;
/** Nejmenší výška pole (px). Na počítači v tabulce aspoň 40, prst 44 řeší třída. */
export const VYSKA_POLE = 40;
/** Vodorovné odsazení buňky a pole z každé strany (px); pole má navíc 1px rámeček. */
export const ODSAZENI_BUNKY = 5;
export const ODSAZENI_POLE = 6;
/** Šířka OKNA, pod kterou jdou data smlouvy (sloupce typu datum) do bubliny ⋯. */
export const MEZ_DAT_DO_BUBLINY = 1100;
/** Tečka stavu + ⋯ + koš i s odsazením (px) — rezerva, kterou tabulka přidá ke sloupcům. */
export const REZIE_RADKU = 130;
/** Do této šířky OKNA mají pole 16 px (index.css, „POLE NA MOBILU“ — jinak
 *  iPhone při kliknutí přiblíží stránku). */
export const MEZ_POLE_16 = 640;
/** Šířky výš jsou spočítané pro 13–14 px písma; na takovém okně je ale pole
 *  16 px a „Kooperativa“ i „1 500 000“ se v něm uřízly (kontrola 5. 10. 2026:
 *  122 a 93 px textu v poli 102 a 78 px). Sloupce se proto rozšíří o 30 %.
 *  Tabulka se na telefonu posouvá do strany, širší sloupec stojí jen posun. */
export const NASOBEK_SIRKY_POLE_16 = 1.3;
/** Šířka sloupce na okně, kde mají pole 16 px. */
export const sirkaPole16 = (sirka) => Math.ceil((+sirka || 150) * NASOBEK_SIRKY_POLE_16);

/* Tučné tabulkové číslice Inter mají ~0,62 em; bereme 0,64 jako rezervu.
   Oddělovač tisíců (nezlomitelná mezera) ~0,28 em. */
const CIFRA_EM = 0.64;
const MEZERA_EM = 0.28;

/**
 * Kolik px potřebuje sloupec, aby se do peněžního pole vešlo `cifer`-místné
 * číslo („123 456 789“ = 9 cifer) v písmu `pismo`, i s odsazením buňky a pole.
 */
export function sirkaNaCastku(cifer, pismo = PISMO_POLE) {
  const n = Math.max(1, Math.floor(+cifer || 1));
  const mezer = Math.floor((n - 1) / 3);
  return Math.ceil(n * CIFRA_EM * pismo + mezer * MEZERA_EM * pismo + 2 * (ODSAZENI_POLE + 1) + 2 * ODSAZENI_BUNKY);
}

/**
 * ÚZKÉ OKNO: data smlouvy (sloupce typu `date`) jdou z řádku do bubliny ⋯,
 * částky, označení a koš zůstanou v řádku (A3.5 — na 1024 px byl koš
 * i „Výročí smlouvy“ mimo obraz a sedm tabulek se posouvalo do strany).
 *
 * PŘESUN NENÍ SMAZÁNÍ: pole se jen kreslí jinde, klíč i zápis zůstávají.
 * V bublině stojí hned za číslem smlouvy (to jmenoval majitel jako první).
 *
 * @param {Array} sloupce  sloupce řádku (sloupceZdroje / sloupceZavazku / sloupceNefin)
 * @param {Array|Function} bublina  pole bubliny — pole, nebo funkce (řádek) → pole
 * @param {{ uzke: boolean }} opts
 * @returns {{ sloupce: Array, bublina: Array|Function }}
 */
export function rozdelPodleSirky(sloupce = [], bublina = [], { uzke = false } = {}) {
  const radek = Array.isArray(sloupce) ? sloupce : [];
  if (!uzke) return { sloupce: radek, bublina };
  const data = radek.filter((s) => s && s.type === "date");
  if (!data.length) return { sloupce: radek, bublina };
  const doBubliny = data.map((s) => ({ k: s.k, l: s.l, type: "date", suffix: undefined, moznosti: undefined, wide: false }));
  const vlozit = (pole) => {
    const seznam = Array.isArray(pole) ? pole.filter((f) => !data.some((d) => d.k === f?.k)) : [];
    const i = seznam.findIndex((f) => f?.k === "cislo");
    return i === -1 ? [...doBubliny, ...seznam] : [...seznam.slice(0, i + 1), ...doBubliny, ...seznam.slice(i + 1)];
  };
  return {
    sloupce: radek.filter((s) => !(s && s.type === "date")),
    bublina: typeof bublina === "function" ? (r) => vlozit(bublina(r)) : vlozit(bublina),
  };
}

/* CO ZŮSTÁVÁ PŘÍMO V ŘÁDKU — přesně to, co majitel nadiktoval, plus dopočet.
 *
 * hodnota, vklad a zam tu MUSÍ zůstat: čte je lib/bilance.js a jsou to údaje,
 * kvůli kterým se schůzka vede. Kdyby se schovaly za klik, poradce by u každé
 * smlouvy klikal — a přesně to už jednou majitel vrátil. */
export const V_RADKU = [
  "oznaceni",
  "instituce",
  "hodnota",
  SLOUPEC_BASE,
  "vklad",
  "zam",
  "zalozeno",
  "pocatek",
  "konecVazaci",
  "vyroci",
];

/* KOMPAKTNÍ ŘÁDEK PRO PREZENTACI (opts.kompaktni).
 *
 * Levý panel prezentace je jen část obrazovky, takže se do něj plný řádek
 * nevejde a poradce by musel na schůzce posouvat myší doprava. Majitel:
 * „i v tom prezenčním režimu, aby to šlo taky vyplnit… stavebko má, napíšu
 * instituci, aktuální hodnotu, datum založení, kolik posílá měsíčně, a hotovo."
 *
 * Zůstává přesně ten scénář. Datum UKONČENÍ (konec vázací doby / výročí) se
 * posouvá do bubliny — na schůzce se dopisuje ze smlouvy spolu s číslem
 * smlouvy, ne z hlavy klienta. V běžném formuláři zůstává v řádku. */
export const V_RADKU_KOMPAKT = [
  "oznaceni",
  "instituce",
  "hodnota",
  SLOUPEC_BASE,
  "vklad",
  "zam",
  "zalozeno",
  "pocatek",
];

/* Pořadí sloupců v ŘÁDKU podle diktátu majitele:
 *   označení | instituce | hodnota | (hodnota v CZK) | vklad | (zaměstnavatel)
 *   | datum založení | datum ukončení
 * „zam" je hned za „vklad", protože obojí je měsíční odkládání a poradce je
 * čte jako dvojici. */
const PORADI = [
  "oznaceni",
  "instituce",
  "hodnota",
  SLOUPEC_BASE,
  "vklad",
  "zam",
  "zalozeno",
  "pocatek",
  "konecVazaci",
  "vyroci",
];

/* Pořadí polí v BUBLINĚ. Majitel jmenoval jako první číslo smlouvy
 * („doplní se číslo smlouvy atd."), poznámka je nejdelší, takže poslední.
 * Data ukončení jsou hned za číslem smlouvy — v kompaktním (prezentačním)
 * režimu se opisují ze stejného papíru. */
const PORADI_BUBLINA = [
  "cislo",
  // „Druh produktu" (penzijko) hned za číslem smlouvy — obojí se opisuje ze
  // stejného papíru. Výš být nemůže: číslo smlouvy jmenoval majitel jako
  // první. Níž ale taky ne — podle druhu se řídí našeptávač instituce v řádku
  // (DPS jen penzijní společnosti, DIP i banka a OCP) i státní příspěvek pod
  // tabulkou, takže schovaný dole by ho poradce na schůzce nenašel.
  "druh",
  "konecVazaci",
  "vyroci",
  "typUctu",
  "typ",
  "strategie",
  "urok",
  "cilovaCastka",
  "likvidni",
  "currency",
  "pozn",
];

/* Popisky, které předloha pojmenovává jinak než sada polí. Popisek se měnit
 * SMÍ (klíč ne) — a „Vaše označení" je srozumitelnější než „Označení". */
const LABEL = {
  oznaceni: "Vaše označení",
  instituce: "Finanční instituce",
  [SLOUPEC_BASE]: "Hodnota v CZK",
  // UAT 7/2026: majitel nechce vodorovné posouvání. Dlouhé nadpisy roztahovaly
  // sloupce víc než jejich obsah — „KONEC VÁZACÍ DOBY" je širší než datum.
  zalozeno: "Založeno",
  pocatek: "Počátek",
  konecVazaci: "Konec fixace",
  zam: "Zaměstnavatel",
};

/* V bublině je místo, takže zkracovat není potřeba — jen měna nemá v sadě polí
 * pod klíčem `currency` žádný popisek (nese ho `mena`). */
const LABEL_BUBLINA = { currency: "Měna" };

/* Kategorie, kde cizí měna nedává smysl — stavební spoření i penzijní produkty
 * jsou české státem podporované smlouvy, vedou se v korunách. Majitel při UAT:
 * „u stavebka není potřeba měna, to kolečko zabírá zbytečně moc místa."
 * Pole se u nich NENABÍZÍ ani v bublině. Data se tím nemažou: kdyby v řádku
 * nějaká měna z historie byla, pole se objeví (viz maCiziMenu). */
const BEZ_MENY = ["stavebko", "penzijko"];

/** Má některý řádek jinou měnu než domácí? Pak je pole měny na místě. */
function maCiziMenu(radky, domaci) {
  const d = String(domaci || "CZK").toUpperCase();
  return (Array.isArray(radky) ? radky : []).some((r) => {
    const c = String(r?.currency || "").trim().toUpperCase();
    return c && c !== d;
  });
}

/* Minimální šířka sloupce v px.
 * UAT 8/2026: „Když je ten text krátký, tak tam není potřeba políčko, co
 * zabírá hodně místa." Datum se do 118 px vejde, částka do 104 px taky,
 * protože jednotka je v hlavičce a nezabírá místo v každé buňce. Označení
 * a instituce dostanou víc — je to text, který se opravdu píše.
 * Nejširší kategorie (penzijko, 7 sloupců) chce ~990 px i s tlačítky; do karty
 * stránky (1272 px) se vejde bez vodorovného posouvání.
 *
 * KOMPAKT je totéž o kus utaženější — do levého panelu prezentace. Míň už ne:
 * pod 128 px se do pole nevejde „ČSOB Stavební spořitelna" ani zkráceně. */
/* PENÍZE (A3.5): sloupec je dimenzovaný na 8místné číslo (do 99 999 999)
 * v písmu, které v něm opravdu je — `sirkaNaCastku`, ne odhad od oka.
 * Hlídá to __tests__/zdrojeSloupce.test.js. */
const SIRKA = { text: 128, money: Math.max(104, sirkaNaCastku(8, PISMO_POLE)), number: 78, date: 118, select: 112, check: 100, mena: 74 };
const SIRKA_KLIC = { oznaceni: 150, instituce: 160, [SLOUPEC_BASE]: 112 };
const SIRKA_KOMPAKT = { text: 116, money: Math.max(96, sirkaNaCastku(8, PISMO_POLE_KOMPAKT)), number: 74, date: 108, select: 104, check: 92, mena: 70 };
const SIRKA_KLIC_KOMPAKT = { oznaceni: 128, instituce: 136, [SLOUPEC_BASE]: 100 };

/** Typ buňky. Peníze se od procent liší formátem i chováním vstupu. */
function typBunky(pole, cur) {
  if (pole.k === "currency") return "mena";
  if (pole.k === SLOUPEC_BASE) return "base";
  if (pole.type === "number") {
    // Peněžní pole poznáme podle symbolu měny v příponě (Kč / € / Kč/m) —
    // stejné pravidlo jako Inp na stránce, ať se formát nikde nerozejde.
    const suf = String(pole.suffix ?? "");
    const symboly = [String(cur ?? ""), "Kč", "€"].filter(Boolean);
    return symboly.some((s) => suf.includes(s)) ? "money" : "number";
  }
  return pole.type || "text";
}

/* Celá sada polí kategorie jako mapa klíč → definice.
 * Jeden zdroj pro řádek i bublinu — proto se odsud nemůže nic ztratit. */
function sadaPoli(kategorie, cur, opts) {
  const pole = polePodleTypu(kategorie, cur);
  const menaPole = pole.find((f) => f.k === "mena");
  // Měnu nabízíme, jen když ji poradce reálně potřebuje.
  const menaDava = !BEZ_MENY.includes(kategorie) || maCiziMenu(opts.radky, opts.domaciMena);

  const podleKlice = new Map();
  for (const f of pole) {
    if (f.k === "mena") continue; // ukládá se `currency` (viz hlavička souboru)
    podleKlice.set(f.k, f);
  }
  // Označení sada polí nemá (nesla ho hlavička karty) — v tabulce je to sloupec.
  if (!podleKlice.has("oznaceni")) podleKlice.set("oznaceni", { k: "oznaceni", type: "text" });
  if (menaDava) {
    podleKlice.set("currency", { k: "currency", type: "select", moznosti: menaPole?.moznosti || MENY });
  }
  // „Hodnota v CZK" má smysl JEN u cizoměnného řádku. U korunového je to totéž
  // číslo dvakrát vedle sebe — majitel: „už není potřeba znovu napsat, že
  // hodnota je 150 000 CZK."
  if (maCiziMenu(opts.radky, opts.domaciMena)) {
    podleKlice.set(SLOUPEC_BASE, { k: SLOUPEC_BASE, type: "base" });
  }
  return podleKlice;
}

/** Seřadí klíče podle daného pořadí; neznámý klíč se nezahodí, jde na konec. */
function serad(klice, poradi) {
  const idx = (k) => {
    const i = poradi.indexOf(k);
    return i === -1 ? poradi.length : i;
  };
  return [...klice].sort((a, b) => idx(a) - idx(b));
}

/**
 * Sloupce tabulky pro jednu kategorii zdrojů — jen to, co je vždy vidět.
 *
 * @param {string} kategorie  stavebko | penzijko | opf | kzp | ucty
 * @param {string} cur        symbol domácí měny (Kč / €) — jen do přípon
 * @param {object} opts       { list, radky, domaciMena, kompaktni }
 *   `list` = id <datalist>u pro sloupec Instituce. Smí to být i FUNKCE
 *   (radek) → id: u penzijka rozhoduje o nabídce druh KAŽDÉHO ŘÁDKU (DPS
 *   jen penzijní společnosti, DIP i banka a OCP), ne celá sekce. Rozbaluje
 *   ji až ZdrojeTabulka, která jediná zná konkrétní řádek.
 * @returns {Array<{k,l,type,suffix,moznosti,list,sirka,ro}>}
 */
export function sloupceZdroje(kategorie, cur = "Kč", opts = {}) {
  const podleKlice = sadaPoli(kategorie, cur, opts);
  const vRadku = opts.kompaktni ? V_RADKU_KOMPAKT : V_RADKU;
  const sirkaTypu = opts.kompaktni ? SIRKA_KOMPAKT : SIRKA;
  const sirkaKlice = opts.kompaktni ? SIRKA_KLIC_KOMPAKT : SIRKA_KLIC;
  const klice = serad([...podleKlice.keys()].filter((k) => vRadku.includes(k)), PORADI);

  return klice.map((k) => {
    const f = podleKlice.get(k);
    const type = typBunky(f, cur);
    // Jednotka patří do HLAVIČKY, ne do každé buňky. Dřív každý řádek opakoval
    // „Kč" / „Kč/m" / „%" — u pěti řádků je to pětkrát tatáž informace a
    // zbytečná šířka. V hlavičce stačí jednou: „Současná hodnota (Kč)".
    const jednotka = String(f.suffix ?? "").trim();
    const popisek = LABEL[k] || f.l || k;
    return {
      k,
      l: jednotka ? `${popisek} (${jednotka})` : popisek,
      /* Popisek a jednotka i ZVLÁŠŤ: tabulka překládá popisek a jednotku
       * přilepí až za překlad. Celý nadpis „Zaměstnavatel (Kč/m)“ ve slovníku
       * není, takže zůstával česky i na vietnamské obrazovce (4. 10. 2026). */
      popisek,
      jednotka,
      type,
      suffix: undefined,
      moznosti: f.moznosti,
      list: k === "instituce" ? opts.list : undefined,
      sirka: sirkaKlice[k] || sirkaTypu[type] || 150,
      ro: type === "base", // dopočet, ne vstup
      // Písmo v poli patří k šířce: kompaktní řádek je užší, proto i menší písmo.
      pismo: opts.kompaktni ? PISMO_POLE_KOMPAKT : PISMO_POLE,
    };
  });
}

/**
 * Pole do bubliny (tlačítko ⋯ na konci řádku) — co se vyplňuje málokdy.
 *
 * Stejný tvar jako sloupce, jen s vlastním pořadím a plnými popisky: v panelu
 * je místo, takže „Číslo smlouvy" se zkracovat nemusí. Přípona (Kč, %) zůstává
 * u pole, protože v bublině není hlavička tabulky, kam by se dala dát.
 *
 * @returns {Array<{k,l,type,suffix,moznosti,wide}>}
 */
export function poleBubliny(kategorie, cur = "Kč", opts = {}) {
  const podleKlice = sadaPoli(kategorie, cur, opts);
  // Doplněk řádku — proto TÝŽ přepínač `kompaktni`. Jinak by pole vypadlo
  // z řádku i z bubliny a poradce by ho neměl kam napsat.
  const vRadku = opts.kompaktni ? V_RADKU_KOMPAKT : V_RADKU;
  const klice = serad([...podleKlice.keys()].filter((k) => !vRadku.includes(k)), PORADI_BUBLINA);

  return klice.map((k) => {
    const f = podleKlice.get(k);
    const type = typBunky(f, cur);
    return {
      k,
      l: LABEL_BUBLINA[k] || f.l || k,
      type,
      suffix: String(f.suffix ?? "").trim() || undefined,
      moznosti: f.moznosti,
      wide: !!f.wide,
    };
  });
}

/** Klíče sloupců — pro testy a kontrolu proti bilanci. */
export const kliceSloupcu = (kategorie, cur = "Kč", opts = {}) => sloupceZdroje(kategorie, cur, opts).map((s) => s.k);

/** Klíče polí v bublině — pro testy. */
export const kliceBubliny = (kategorie, cur = "Kč", opts = {}) => poleBubliny(kategorie, cur, opts).map((s) => s.k);

/* ===================================================================
   NEFINANČNÍ MAJETEK — tatáž tabulka, jen sada polí je po ŘÁDCÍCH

   NÁLEZ UAT 8/2026 (screenshot z prezentace): zatímco stavebko, penzijko,
   investice, ŽP i účty už byly kompaktní tabulka, nefinanční majetek zůstal
   OBŘÍ KARTA — jedna položka zabrala skoro celou obrazovku. Majitel:
   „v prezenčním stylu je to pořád takhle velký, musí to být zmenšený.“

   PROČ TO NEBYLO HOTOVÉ DŘÍV — a proč ten důvod neobstál:
   sada polí u nefinančního majetku nepatří KATEGORII, ale TYPU KAŽDÉ POLOŽKY
   (byt na pronájem má měsíční příjem, auto ne). Z toho se usoudilo, že
   „společná hlavička tabulky neexistuje“. Existuje: hlavička je SJEDNOCENÍ
   toho, co dává smysl napříč typy, a co daný typ nemá, je PRÁZDNÁ BUŇKA.

   ⚠️ Prázdná buňka, ne zmizelý sloupec. Kdyby si každý řádek kreslil vlastní
   sadu sloupců, řádky by se rozjely a hlavička by přestala sedět na obsah.
   A prázdno je „—", nikdy 0 — nula je tvrzení „auto nevynáší nic", které
   nikdo neřekl.

   ⚠️ KLÍČE SE ZASE NEPŘEJMENOVÁVAJÍ. Přesun adresy, poznámky nebo pojištění
   do bubliny NENÍ smazání dat: klíč i zápis (setM) zůstávají, mění se jen
   místo vykreslení. Sada polí zůstává majetkem typu (nefinancni.js).
=================================================================== */

/* Sloupec `typ` v sadě polí typu není — tu sadu teprve vybírá. V kartě to byla
 * samostatná hlavička nad poli; v tabulce je to sloupec jako každý jiný.
 * Klíč `typ` je přesně ten, který čte propsaniMajetku.typNefin(). */
export const SLOUPEC_TYP = "typ";

/* CO ZŮSTÁVÁ V ŘÁDKU: název, typ, hodnota a měsíční částka — tedy to, kvůli
 * čemu se položka do analýzy vůbec píše. Měsíční příjem A měsíční náklady jsou
 * dva sloupce schválně: jeden sloupec „měsíčně" by u pronájmu znamenal příjem
 * a u komerčního objektu náklad, takže by hlavička lhala. Kde typ pole nemá,
 * zůstane buňka prázdná. */
export const V_RADKU_NEFIN = [
  "oznaceni",
  SLOUPEC_TYP,
  "hodnota",
  SLOUPEC_BASE,
  "prijem",
  "naklady",
];

/* Pořadí polí v BUBLINĚ: čím se položka identifikuje (adresa, SPZ, IČO…),
 * pak termíny, pak zbytek. Pojištění, měna a poznámka na konec — stejně jako
 * u finančních kategorií je poznámka poslední a přes celou šířku. */
const PORADI_BUBLINA_NEFIN = [
  "adresa",
  "katastr",
  "spz",
  "ico",
  "popis",
  "porizeno",
  "najemDo",
  "ocekavano",
  "rokVyroby",
  "vymera",
  "podil",
  "jistota",
  "pojisteno",
  "currency",
  "pozn",
];

/* Popisky, které předloha pojmenovává jinak než sada polí (klíč se nemění). */
const LABEL_NEFIN = { oznaceni: "Vaše označení", [SLOUPEC_TYP]: "Typ majetku" };

/* Typ majetku je dlouhý text („Cennosti (hodinky, umění, zlato)"), proto
 * nejširší sloupec hned po označení. Zbytek jede na společných šířkách. */
const SIRKA_KLIC_NEFIN = { oznaceni: 150, [SLOUPEC_TYP]: 168, [SLOUPEC_BASE]: 112 };
const SIRKA_KLIC_NEFIN_KOMPAKT = { oznaceni: 128, [SLOUPEC_TYP]: 144, [SLOUPEC_BASE]: 100 };

/** `mena` je popisek sady polí, ukládá se `currency` — jako u finančních. */
const naKlicDat = (k) => (k === "mena" ? "currency" : k);

/**
 * SJEDNOCENÍ polí přes všechny typy majetku → mapa klíč → definice.
 * Jeden zdroj pro hlavičku i pro bublinu, takže se odsud nemůže nic ztratit.
 * První výskyt vyhrává: popisek „Adresa" je u bytu i u komerčních prostor týž.
 */
function sadaPoliNefin(cur, opts) {
  const lang = opts.lang || "cs";
  const podleKlice = new Map();
  podleKlice.set("oznaceni", { k: "oznaceni", type: "text", l: LABEL_NEFIN.oznaceni });
  podleKlice.set(SLOUPEC_TYP, {
    k: SLOUPEC_TYP,
    type: "select",
    l: LABEL_NEFIN[SLOUPEC_TYP],
    moznosti: nabidkaTypu(lang).map((o) => ({ v: o.k, l: o.l })),
  });
  for (const t of TYPY_NEFIN) {
    for (const f of poleNefin(t, cur)) {
      if (f.k === "mena") continue; // ukládá se `currency`
      if (!podleKlice.has(f.k)) podleKlice.set(f.k, f);
    }
  }
  /* NADPIS SLOUPCE PŘÍJMU (14. 10. 2026): sloupec je společný pro všechny
   * typy, takže „první výskyt vyhrává“ by z něj udělal „Hrubý nájem“ i nad
   * firmou. Proto podle ŘÁDKŮ: všechny řádky s příjmem jsou pronájem nebo
   * komerční prostory → „Hrubý nájem“ (majitel: ptát se klienta na HRUBÝ
   * nájem), jinak (firma, prázdná tabulka) obecný „Měsíční příjem“. */
  const prijemPole = podleKlice.get("prijem");
  if (prijemPole) {
    const typy = (Array.isArray(opts.radky) ? opts.radky : [])
      .map((r) => String(r?.[SLOUPEC_TYP] ?? "").trim())
      .filter((t) => maPrijem(t));
    const jenNajem = typy.length > 0 && typy.every((t) => jeNajemni(t));
    podleKlice.set("prijem", { ...prijemPole, l: jenNajem ? POPISEK_PRIJMU.najem : POPISEK_PRIJMU.obecny });
  }
  // Nemovitost v zahraničí i zlato v dolarech jsou běžné — měnu nabízíme vždy.
  const menaPole = poleNefin(NEMOVITOST_BYDLENI, cur).find((f) => f.k === "mena");
  podleKlice.set("currency", { k: "currency", type: "select", moznosti: menaPole?.moznosti || MENY });
  // Dopočet do CZK má smysl jen u cizoměnného řádku (jinak totéž číslo dvakrát).
  if (maCiziMenu(opts.radky, opts.domaciMena)) {
    podleKlice.set(SLOUPEC_BASE, { k: SLOUPEC_BASE, type: "base" });
  }
  return podleKlice;
}

/**
 * Sloupce tabulky nefinančního majetku — SPOLEČNÉ pro všechny řádky.
 * Hlavička se tedy nemění s typem položky; co typ nemá, řeší radekMaPoleNefin.
 *
 * @param {string} cur   symbol domácí měny (Kč / €)
 * @param {object} opts  { radky, domaciMena, lang, kompaktni }
 */
export function sloupceNefin(cur = "Kč", opts = {}) {
  const podleKlice = sadaPoliNefin(cur, opts);
  const sirkaTypu = opts.kompaktni ? SIRKA_KOMPAKT : SIRKA;
  const sirkaKlice = opts.kompaktni ? SIRKA_KLIC_NEFIN_KOMPAKT : SIRKA_KLIC_NEFIN;
  const klice = serad([...podleKlice.keys()].filter((k) => V_RADKU_NEFIN.includes(k)), V_RADKU_NEFIN);

  return klice.map((k) => {
    const f = podleKlice.get(k);
    const type = typBunky(f, cur);
    const jednotka = String(f.suffix ?? "").trim();
    const popisek = LABEL_NEFIN[k] || f.l || k;
    return {
      k,
      l: jednotka ? `${popisek} (${jednotka})` : popisek,
      popisek,   // zvlášť kvůli překladu — viz sloupceZdroje
      jednotka,
      type,
      suffix: undefined, // jednotka je v hlavičce, ne v každé buňce
      moznosti: f.moznosti,
      sirka: sirkaKlice[k] || sirkaTypu[type] || 150,
      ro: type === "base",
      pismo: opts.kompaktni ? PISMO_POLE_KOMPAKT : PISMO_POLE,
    };
  });
}

/**
 * Pole do bubliny PODLE TYPU ŘÁDKU — auto dostane SPZ, byt adresu.
 * Bublina je jediné místo, kde se sada polí smí lišit řádek od řádku: hlavička
 * tabulky se tím nehne, takže se řádky nemají jak rozjet.
 */
export function poleBublinyNefin(typ, cur = "Kč", opts = {}) {
  const podleKlice = sadaPoliNefin(cur, opts);
  const typove = new Set(kliceNefin(typ, cur).map(naKlicDat));
  typove.add("currency"); // měna se nabízí u každého typu
  const klice = serad(
    [...podleKlice.keys()].filter((k) => typove.has(k) && !V_RADKU_NEFIN.includes(k)),
    PORADI_BUBLINA_NEFIN,
  );

  return klice.map((k) => {
    const f = podleKlice.get(k);
    return {
      k,
      l: LABEL_BUBLINA[k] || f.l || k,
      type: typBunky(f, cur),
      suffix: String(f.suffix ?? "").trim() || undefined,
      moznosti: f.moznosti,
      wide: !!f.wide,
    };
  });
}

/**
 * Má položka daného typu tohle pole? Řídí, jestli se v buňce kreslí vstup,
 * nebo prázdné „—".
 *
 * Bez tohohle by se poradce u auta ptal, kolik měsíčně vynáší — přesně ten
 * nález UAT, kvůli kterému nefinancni.js vznikl.
 */
export function radekMaPoleNefin(typ, klic, cur = "Kč") {
  const k = String(klic ?? "");
  if (k === SLOUPEC_TYP || k === SLOUPEC_BASE || k === "currency") return true;
  return kliceNefin(typ, cur).map(naKlicDat).includes(k);
}

/** Klíče sloupců / polí v bublině nefinančního majetku — pro testy. */
export const kliceSloupcuNefin = (cur = "Kč", opts = {}) => sloupceNefin(cur, opts).map((s) => s.k);
export const kliceBublinyNefin = (typ, cur = "Kč", opts = {}) => poleBublinyNefin(typ, cur, opts).map((s) => s.k);
